import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, Skull, Loader, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Officer Roster — live list of your named zombies.
 * - Alive officers get a specialty badge (OVERWHELM / AMBUSH / SIEGE)
 * - Fallen roster hidden behind a toggle (Memorial)
 * - Shows kills-until-next-officer progress
 */
export default function OfficerRoster() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFallen, setShowFallen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/officers/roster");
        if (!cancelled) setData(data);
      } catch (e) { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    // refresh when battle resolves
    const on = () => api.get("/officers/roster").then(({ data }) => setData(data)).catch(() => {});
    window.addEventListener("game-state-changed", on);
    return () => { cancelled = true; window.removeEventListener("game-state-changed", on); };
  }, []);

  if (loading || !data) return null;
  const { alive = [], fallen = [], total_kills = 0, kills_remaining = 100, spawn_interval = 100 } = data;

  return (
    <div className="narrative-panel" data-testid="officer-roster">
      <div className="flex items-center justify-between mb-3">
        <p className="tac-label flex items-center gap-2">
          <Users className="w-3 h-3" /> NAMED OFFICERS
        </p>
        <p className="font-mono text-[0.6rem] text-muted-h tracking-widest">
          {alive.length} STANDING · {fallen.length} FALLEN
        </p>
      </div>

      {alive.length === 0 ? (
        <p className="text-xs text-muted-h italic mb-3">
          Zombies don't have names yet. Kill enough humans and one of your dead will start to remember its own.
        </p>
      ) : (
        <ul className="space-y-2 mb-3" data-testid="officer-roster-alive">
          {alive.map((o) => (
            <li key={o.id} className="flex items-center justify-between border-l-2 border-blood/50 pl-3 py-1"
                data-testid={`officer-${o.id}`}>
              <div>
                <p className="font-serif-h text-lg text-bone leading-tight">{o.name}</p>
                <p className="font-mono text-[0.6rem] text-muted-h tracking-widest">
                  {(o.specialty || "").toUpperCase()} · {o.kills || 0} KILLS
                </p>
              </div>
              <span className="font-mono text-[0.55rem] tracking-widest text-blood/70 border border-blood/40 px-2 py-0.5">
                +{Math.round(0.05 * 100)}% {(o.specialty || "").toUpperCase()}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Next officer progress */}
      <div className="border-t border-[var(--border-subtle)] pt-3">
        <div className="flex items-baseline justify-between mb-1">
          <span className="font-mono text-[0.6rem] tracking-widest text-muted-h">NEXT OFFICER AT</span>
          <span className="font-mono text-[0.7rem] text-bone tabular-nums">
            {total_kills.toLocaleString()} / {(total_kills + kills_remaining).toLocaleString()}
          </span>
        </div>
        <div className="bar-track">
          <div className="bar-fill"
               style={{ width: `${100 - Math.round((kills_remaining / spawn_interval) * 100)}%` }}
               data-testid="officer-progress-bar" />
        </div>
        <p className="text-[0.6rem] text-muted-h mt-1 italic">
          {kills_remaining} more kills and another of your dead may wake.
        </p>
      </div>

      {/* Fallen */}
      {fallen.length > 0 && (
        <div className="border-t border-[var(--border-subtle)] pt-3 mt-3">
          <button onClick={() => setShowFallen((v) => !v)}
                  className="w-full flex items-center justify-between text-left"
                  data-testid="officer-roster-fallen-toggle">
            <span className="tac-label flex items-center gap-2">
              <Skull className="w-3 h-3" /> MEMORIAL · {fallen.length}
            </span>
            {showFallen ? <ChevronUp className="w-3 h-3 text-muted-h" /> : <ChevronDown className="w-3 h-3 text-muted-h" />}
          </button>
          {showFallen && (
            <ul className="space-y-3 mt-3" data-testid="officer-roster-fallen">
              {fallen.map((o) => (
                <li key={o.id} className="border-l-2 border-blood/30 pl-3 py-1 opacity-70"
                    data-testid={`fallen-${o.id}`}>
                  <p className="font-serif-h text-base text-bone/80 leading-tight italic">
                    {o.name}
                  </p>
                  <p className="font-mono text-[0.55rem] text-muted-h tracking-widest mt-0.5">
                    {(o.specialty || "").toUpperCase()} · FELL AT {o.died_battle || "the field"}
                  </p>
                  {o.epitaph && (
                    <p className="text-[0.7rem] text-muted-h italic mt-1 leading-snug">
                      "{o.epitaph}"
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
